import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchNodeTimeseries } from '../../api';
import InfoHint from '../InfoHint';
import { AXIS, CATEGORICAL_PALETTE } from '../nodes/nodeChartTheme';
import useNodeRequest from '../nodes/useNodeRequest';
import { ErrorText, GranularitySelect, MetricToggle, MutedText } from './callTypesControls';
import { buildCategoryTrend, callsNoun, defaultGranularity, formatCount } from './callTypesModel';

const METRICS = [
  { id: 'calls', label: 'Звонки' },
  { id: 'share', label: 'Доля' }
];

const KIND_LABELS = {
  types: { plural: 'типов', genitive: 'типа', accusative: 'типы', dative: 'типу' },
  outcomes: { plural: 'итогов', genitive: 'итога', accusative: 'итоги', dative: 'итогу' }
};

/** Динамика всех значений выхода: подпись под графиком скрывает и показывает линию. */
const AllCategoriesTrend = ({ dateRange, callType, chart, items, emphasisCode, kind }) => {
  const labels = KIND_LABELS[kind];
  const [granularity, setGranularity] = useState(() => defaultGranularity(dateRange));
  const [metric, setMetric] = useState('calls');
  const [hiddenCodes, setHiddenCodes] = useState([]);

  const params = useMemo(
    () => ({
      node_id: chart.nodeId,
      output_key: chart.outputKey,
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      call_type: callType === 'all' ? undefined : callType,
      granularity
    }),
    [chart, dateRange, callType, granularity]
  );
  const { data, isLoading, error } = useNodeRequest({
    fetcher: fetchNodeTimeseries,
    params,
    errorText: `Не удалось загрузить динамику ${labels.plural}.`
  });

  const rows = useMemo(
    () => buildCategoryTrend({ points: Array.isArray(data) ? data : [], items, dateRange, granularity }),
    [data, items, dateRange, granularity]
  );
  const series = items.map((item, index) => ({
    ...item,
    index,
    color: CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]
  }));

  const toggleLine = (code) =>
    setHiddenCodes((current) => (current.includes(code) ? current.filter((item) => item !== code) : [...current, code]));

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="text-base font-semibold">Динамика всех {labels.plural}</h3>
        <div className="flex flex-wrap gap-2">
          <GranularitySelect
            trackId="call_paths.category_trend.granularity"
            label={`Шаг динамики ${labels.plural}`}
            value={granularity}
            onChange={setGranularity}
          />
          <MetricToggle trackId="call_paths.category_trend.metric" options={METRICS} value={metric} onChange={setMetric} />
        </div>
      </div>
      <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
        {metric === 'calls'
          ? `Число звонков каждого ${labels.genitive} за период.`
          : `Доля от звонков, проанализированных по ${labels.dative} в каждом периоде.`}
        <InfoHint
          text={`Доля считается от числа звонков, проанализированных по ${labels.dative} за каждый день, неделю или месяц. У одного звонка может быть несколько значений, поэтому сумма долей может быть больше 100%.`}
          label={`Как считается доля ${labels.plural}`}
          trackId="call_paths.category_trend.hint"
        />
      </p>
      {error && <ErrorText>{error}</ErrorText>}
      {!error && (isLoading || !data) && <MutedText>Загружаем динамику {labels.plural}…</MutedText>}
      {!error && !isLoading && data && (
        <>
          <div className="mt-4 h-64 min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
              <LineChart data={rows} margin={{ top: 10, right: 14, bottom: 8, left: -10 }}>
                <CartesianGrid {...AXIS.grid} vertical={false} />
                <XAxis dataKey="label" tick={AXIS.tick} minTickGap={18} />
                <YAxis
                  width={45}
                  allowDecimals={metric === 'share'}
                  domain={metric === 'share' ? [0, 100] : [0, 'auto']}
                  tick={AXIS.tick}
                  tickFormatter={metric === 'share' ? (value) => `${value}%` : undefined}
                />
                <Tooltip
                  wrapperStyle={AXIS.tooltipWrapperStyle}
                  formatter={(value, name) => [
                    metric === 'share'
                      ? `${Number(value).toFixed(1).replace('.', ',')}%`
                      : `${formatCount(value)} ${callsNoun(value)}`,
                    name
                  ]}
                />
                {series
                  .filter(({ code }) => !hiddenCodes.includes(code))
                  .map(({ code, index, label, color }) => (
                    <Line
                      key={code}
                      type="linear"
                      dataKey={`${metric}_${index}`}
                      name={label}
                      stroke={color}
                      strokeWidth={emphasisCode === code ? 3 : 2}
                      strokeOpacity={emphasisCode && emphasisCode !== code ? 0.48 : 1}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 max-h-24 overflow-y-auto" role="group" aria-label={`Линии на графике: ${labels.accusative}`}>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {series.map(({ code, label, color }) => (
                <button
                  key={code}
                  type="button"
                  data-ev="call_paths.category_trend.legend_toggle"
                  aria-pressed={!hiddenCodes.includes(code)}
                  onClick={() => toggleLine(code)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-xs hover:bg-muted/50 ${
                    hiddenCodes.includes(code) ? 'text-muted-foreground opacity-50' : ''
                  }`}
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllCategoriesTrend;
